import { isLogicalOperator, LogicalOperator } from "./LogicalOperator";
import { isVariableDto, VariableDto } from "./VariableDto";

/**
 * Represents a single where clause operation.
 *
 * @interface WhereClauseOp
 * @property id - Unique identifier for this clause.
 * @property left - Variable being evaluated in the clause.
 * @property comparator - Name of the comparison operator (e.g., "EQ", "IN").
 * @property right - Value(s) to compare against (newline-separated for groups).
 * @property precedingLogic - Logical operator preceding this clause (AND/OR). Not applicable for the first clause.
 */
export interface WhereClauseOp {
  id: string;
  left: VariableDto;
  comparator?: string;
  right?: string;
  precedingLogic?: LogicalOperator;
}

/**
 * Type guard for WhereClauseOp.
 *
 * @param obj - Value to test
 * @returns True when obj is a valid WhereClauseOp
 */
export const isWhereClauseOp = (obj: unknown): obj is WhereClauseOp => {
  // Check object
  if (typeof obj !== "object" || obj === null) return false;

  // Check basic props
  const chkObj = obj as WhereClauseOp;
  if (typeof chkObj.id !== "string") return false;
  if (typeof chkObj.left !== "object" || !isVariableDto(chkObj.left)) return false;
  if (chkObj.comparator !== undefined && typeof chkObj.comparator !== "string") return false;
  if (chkObj.right !== undefined && typeof chkObj.right !== "string") return false;
  if (chkObj.precedingLogic !== undefined && !isLogicalOperator(chkObj.precedingLogic))
    return false;

  // Everything is good
  return true;
};

/**
 * Type guard determining whether the provided object is a WhereClauseOp
 * or a form state object. This is useful when handling data that could be
 * either a complete WhereClauseOp or partial form state during editing.
 *
 * @param data - Object to check
 * @returns True when data is a WhereClauseOp (contains left property)
 */
export const isWhereClause = <T extends { comparator?: string; value?: string }>(
  data: WhereClauseOp | T,
): data is WhereClauseOp => "left" in data;
